import React, { useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import {
  type AssetProjectUpdateDto
} from '../../../api-schema-types/assets/AssetProjectCrudDtos'
import { useGetProjectQuery } from '../../../api-services/assets/projectApi'
import { Button, Paper, Box, Grid, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Refresh from '@mui/icons-material/Refresh'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'

import ErrorComponent from '../../components/ui-widgets/ErrorComponent'
import LoadingComponent from '../../components/ui-widgets/LoadingComponent'
import EditSolarProjectForm from './EditSolarProjectForm'
import { ProjectStatusChip } from '../../components/chips/ProjectStatusChip'
import {
  resetEditSolarProject,
  selectCurrentUrlSlug, setCurrentUrlSlug
} from './editSolarProjectSlice'

const EditSolarProjectPage: React.FC = () => {
  const params = useParams<any>()
  const history = useHistory()
  const dispatch = useAppDispatch()

  const currentUrlSlug = useAppSelector<string | null>(selectCurrentUrlSlug)

  let projectSlug: string | null = null
  let project: AssetProjectUpdateDto | null = null

  // ================================================================================================================
  // Initialise params

  if (Boolean(params.projectSlug) && projectSlug !== '') {
    projectSlug = params.projectSlug
  }

  // ================================================================================================================
  // GET Query

  const skipQuery = projectSlug === null
  const getProjectApiResult = useGetProjectQuery({ projectUrlSlug: projectSlug }, { skip: skipQuery })
  const refetchProject = getProjectApiResult.refetch

  if (getProjectApiResult.error !== null && !getProjectApiResult.isLoading && (getProjectApiResult.data != null)) {
    project = getProjectApiResult.data
  }

  useEffect(() => {
    if (currentUrlSlug !== projectSlug) {
      dispatch(resetEditSolarProject())
      dispatch(setCurrentUrlSlug(projectSlug))
    }
  }, [getProjectApiResult.isSuccess])

  // ================================================================================================================
  // Handlers

  const handleRefetch = async (): Promise<void> => {
    await refetchProject()
  }

  const isProjectDataLoaded = (): boolean => {
    if (getProjectApiResult.error != null) return false
    if (getProjectApiResult.isLoading) return false
    if (getProjectApiResult.isFetching) return false
    return true
  }

  // ================================================================================================================

  return (
    <>
      <Grid container>
        <Grid item lg={12}>
          <Grid container sx={{ mb: 4 }}>
            <Grid item lg={8}>
              <Typography variant={'h5'}>
                {' '}
                {project?.projectId} - {(project !== null) ? `${project?.name} ` : ''} [{project?.solarProjectId}]
                {(project !== null)
                  ? (
                    <>
                      &nbsp;
                      &nbsp;
                      <ProjectStatusChip projectStatus={project.projectStatus}/>
                    </>
                    )
                  : ''
                }
              </Typography>
            </Grid>
            <Grid item lg={4} sx={{ textAlign: 'right' }}>
              <Button variant={'outlined'} onClick={() => history.push('/app/projects')} startIcon={<ArrowBackIcon/>}>
                Projects
              </Button>
            </Grid>
          </Grid>

          <Paper>
            <Box sx={{ p: 4, mt: 3 }}>
              {getProjectApiResult.isLoading && <LoadingComponent/>}
              {(getProjectApiResult.error != null) &&
                <ErrorComponent message={RtkQueryHelper.mapResponseError(getProjectApiResult.error)}/>}

              <Grid container justifyContent="right" sx={{ mb: 2 }}>
                <Grid item>
                  <Grid container spacing={2}>
                    <Grid item>
                      <Button variant={'contained'} type="button" onClick={handleRefetch}>
                        <Refresh/>
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>

              </Grid>

              { isProjectDataLoaded() && getProjectApiResult.data !== null && getProjectApiResult.data !== undefined && (
                <EditSolarProjectForm project={getProjectApiResult.data} triggerRefetch={handleRefetch} />
              )}

            </Box>

            {/* ==================================================================================================== */}

          </Paper>

        </Grid>
      </Grid>
    </>
  )
}

export default EditSolarProjectPage
